import { ColorScheme } from "./color_scheme.js";
import { RoundedRectangle } from "./rounded_rectangle.js";
import { TextStyleScheme } from "./text_style_scheme.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "./ui_text_element.js";

export class NotificationBadge extends RoundedRectangle
{
  private static readonly REFERENCE_SIZE = 84;

  private static readonly BODY_TOP = "#FF4148";
  private static readonly BODY_BOTTOM = "#C70F1C";
  private static readonly RIM = "#8C0A14";
  private static readonly TEXT_OUTLINE = "#7A0610";
  private static readonly SHADOW = "rgba(40, 0, 6, 0.45)";

  private static readonly RIM_W = 4;
  private static readonly SHADOW_BLUR = 8;
  private static readonly SHADOW_OFFSET_Y = 5;
  private static readonly FONT_SIZE = 48;
  private static readonly OUTLINE_W = 5;
  private static readonly TEXT_SHADOW_OFFSET_Y = 3;
  private static readonly TEXT_PAD_X = 10;

  private static readonly HIGHLIGHT_W = 0.62;
  private static readonly HIGHLIGHT_H = 0.34;
  private static readonly HIGHLIGHT_TOP = 0.1;

  private readonly count_text: UITextElement;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number )
  {
    const k = Math.min( w, h ) / NotificationBadge.REFERENCE_SIZE;
    const radius = Math.min( w, h ) / 2;
    const rim_w = NotificationBadge.RIM_W * k;
    const shadow_blur = NotificationBadge.SHADOW_BLUR * k;
    const shadow_offset_y = NotificationBadge.SHADOW_OFFSET_Y * k;

    super(
      ctx,
      x,
      y,
      w,
      h,
      radius,
      { type: "linear-gradient", from: NotificationBadge.BODY_TOP, to: NotificationBadge.BODY_BOTTOM },
      undefined,
      false,
      NotificationBadge.RIM,
      rim_w,
      { color: NotificationBadge.SHADOW, blur: shadow_blur, offsetY: shadow_offset_y }
    );

    const highlight_w = w * NotificationBadge.HIGHLIGHT_W;
    const highlight_h = h * NotificationBadge.HIGHLIGHT_H;
    const highlight_x = ( w - highlight_w ) / 2;
    const highlight_y = h * NotificationBadge.HIGHLIGHT_TOP;
    const highlight_radius = Math.min( highlight_w, highlight_h ) / 2;

    const highlight = new RoundedRectangle(
      ctx,
      highlight_x,
      highlight_y,
      highlight_w,
      highlight_h,
      highlight_radius,
      { type: "linear-gradient", from: "rgba(255, 255, 255, 0.45)", to: "rgba(255, 255, 255, 0)" }
    );
    highlight.setClickable( false );
    this.addChild( highlight, true );

    const font_size = NotificationBadge.FONT_SIZE * k;
    const outline_w = NotificationBadge.OUTLINE_W * k;
    const text_shadow_y = NotificationBadge.TEXT_SHADOW_OFFSET_Y * k;
    const text_pad_x = NotificationBadge.TEXT_PAD_X * k;
    const text_x = text_pad_x;
    const text_w = w - text_pad_x * 2;

    this.count_text = new UITextElement(
      ctx,
      text_x,
      0,
      text_w,
      h,
      {
        text: "",
        font_size,
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: outline_w,
        outline_color: NotificationBadge.TEXT_OUTLINE,
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: text_shadow_y,
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    this.count_text.setClickable( false );
    this.addChild( this.count_text, true );

    this.setClickable( false );
  }

  public setCount( count: number ): void
  {
    this.count_text.setText( String( count ) );
  }

  public name(): string
  {
    return "NotificationBadge";
  }
}
